import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import type { components } from "../../lib/api-types.gen";
import {
  adminActivatePath,
  adminCreatePath,
  adminDeactivatePath,
  adminItemPath,
  adminListPath,
  adminMasterDataKeys,
  masterDataInvalidationKeys,
  type MasterDataKind,
} from "./helpers";

/* Contract-derived row types (ADR-S1-005). The admin list of areas/tags carries
 * extra management fields (AdminArea.responsibleUserId, AdminTag stats);
 * categories/severities reuse the public DTO. */
export type AdminCategory = components["schemas"]["Category"];
export type AdminArea = components["schemas"]["AdminArea"];
export type AdminSeverity = components["schemas"]["Severity"];
export type AdminTag = components["schemas"]["AdminTag"];

/** Any create/update body the four kinds accept. */
export type MasterDataBody =
  | components["schemas"]["CreateCategoryRequest"]
  | components["schemas"]["CreateAreaRequest"]
  | components["schemas"]["CreateSeverityRequest"]
  | components["schemas"]["CreateTagRequest"];

const MASTER_DATA_STALE = 5 * 60_000;

function invalidate(queryClient: QueryClient, kind: MasterDataKind): void {
  for (const key of masterDataInvalidationKeys(kind)) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/* ---------------------------------------------------------------------------
 * List hooks - one per kind, ALL rows (active + inactive) for the management
 * table. Each keeps its own row DTO.
 * ------------------------------------------------------------------------- */

export function useAdminCategories() {
  return useQuery({
    queryKey: adminMasterDataKeys.list("categories"),
    queryFn: () => apiFetch<AdminCategory[]>(adminListPath("categories")),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useAdminAreas() {
  return useQuery({
    queryKey: adminMasterDataKeys.list("areas"),
    queryFn: () => apiFetch<AdminArea[]>(adminListPath("areas")),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useAdminSeverities() {
  return useQuery({
    queryKey: adminMasterDataKeys.list("severities"),
    queryFn: () => apiFetch<AdminSeverity[]>(adminListPath("severities")),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useAdminTags() {
  return useQuery({
    queryKey: adminMasterDataKeys.list("tags"),
    queryFn: () => apiFetch<AdminTag[]>(adminListPath("tags")),
    staleTime: MASTER_DATA_STALE,
  });
}

/** GET /admin/tags/stale - [GR-008] "Pflegeverdaechtige Tags", longest unused first. */
export function useStaleTags() {
  return useQuery({
    queryKey: adminMasterDataKeys.staleTags,
    queryFn: () => apiFetch<AdminTag[]>("/admin/tags/stale"),
  });
}

/* ---------------------------------------------------------------------------
 * Mutations - generic over the kind. Every success invalidates the admin list
 * and the public master-data queries (name resolution + active picker).
 * ------------------------------------------------------------------------- */

export function useCreateMasterData(kind: MasterDataKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MasterDataBody) =>
      apiFetch(adminCreatePath(kind), {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidate(queryClient, kind),
  });
}

export interface UpdateMasterDataVars {
  id: string;
  body: MasterDataBody;
}

export function useUpdateMasterData(kind: MasterDataKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: UpdateMasterDataVars) =>
      apiFetch(adminItemPath(kind, id), {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidate(queryClient, kind),
  });
}

export function useDeactivateMasterData(kind: MasterDataKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(adminDeactivatePath(kind, id), { method: "POST" }),
    onSuccess: () => invalidate(queryClient, kind),
  });
}

export function useActivateMasterData(kind: MasterDataKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(adminActivatePath(kind, id), { method: "POST" }),
    onSuccess: () => invalidate(queryClient, kind),
  });
}
